import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import { BoltIcon } from '@heroicons/react/24/solid';
import {
  HomeIcon,
  Squares2X2Icon,
  BellAlertIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  ChartBarIcon,
  UsersIcon,
  GlobeAltIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { menuConfig, ROUTES } from 'config/routes';
import { usePermissions } from 'hooks/usePermissions';
import styles from './Sidebar.module.css';

const { Sider } = Layout;

const LOGO_TEXT = 'Push Notify';

const FADE = 250;
const SIDER_SHRINK_AT = 550;
const SIDER_EXPAND_DUR = 280;

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const iconMap: Record<string, React.ReactNode> = {
  DashboardOutlined: <span className="menu-icon"><HomeIcon /></span>,
  AppstoreOutlined: <span className="menu-icon"><Squares2X2Icon /></span>,
  NotificationOutlined: <span className="menu-icon"><BellAlertIcon /></span>,
  RocketOutlined: <span className="menu-icon"><RocketLaunchIcon /></span>,
  BookOpenIcon: <span className="menu-icon"><BookOpenIcon /></span>,
  GroupOutlined: <span className="menu-icon"><UserGroupIcon /></span>,
  BarChartOutlined: <span className="menu-icon"><ChartBarIcon /></span>,
  UserOutlined: <span className="menu-icon"><UsersIcon /></span>,
  ThunderboltOutlined: <span className="menu-icon"><BoltIcon /></span>,
  GlobalOutlined: <span className="menu-icon"><GlobeAltIcon /></span>,
};

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { filterRoutes } = usePermissions();

  const [logoCollapsed, setLogoCollapsed] = useState(collapsed);
  const [siderCollapsed, setSiderCollapsed] = useState(collapsed);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (collapsed) {
      setLogoCollapsed(true);
      timerRef.current = setTimeout(() => setSiderCollapsed(true), SIDER_SHRINK_AT);
    } else {
      setSiderCollapsed(false);
      timerRef.current = setTimeout(() => setLogoCollapsed(false), SIDER_EXPAND_DUR);
    }

    return () => clearTimeout(timerRef.current);
  }, [collapsed]);

  const textStyle: React.CSSProperties = {
    opacity: logoCollapsed ? 0 : 1,
    transition: `opacity ${FADE}ms ease`,
    transitionDelay: logoCollapsed ? '0ms' : `${FADE}ms`,
  };

  const boltStyle: React.CSSProperties = {
    opacity: logoCollapsed ? 1 : 0,
    transition: `opacity ${FADE}ms ease`,
    transitionDelay: logoCollapsed ? `${FADE}ms` : '0ms',
  };

  const menuItems = useMemo(() => {
    const filteredRoutes = filterRoutes(menuConfig);

    return filteredRoutes.map(route => {
      const item: any = {
        key: route.path,
        icon: route.icon ? iconMap[route.icon] : null,
        label: route.title,
      };

      if (route.children && route.children.length > 0) {
        item.children = route.children.map(child => ({
          key: child.path,
          label: child.title,
        }));
      }

      return item;
    });
  }, [filterRoutes]);

  const selectedKey = useMemo(() => {
    const paths = location.pathname.split('/').filter(Boolean);
    if (paths.length === 0) return ROUTES.DASHBOARD;

    const exactMatch = menuItems.find(item => item.key === location.pathname);
    if (exactMatch) return exactMatch.key;

    const parentPath = `/${paths[0]}`;
    const parentMatch = menuItems.find(item => item.key === parentPath);
    if (parentMatch) {
      if (parentMatch.children) {
        const childMatch = parentMatch.children.find(
          (child: any) => child.key === location.pathname
        );
        if (childMatch) return childMatch.key;
      }
      return parentMatch.key;
    }

    return ROUTES.DASHBOARD;
  }, [location.pathname, menuItems]);

  const openKeys = useMemo(() => {
    const paths = location.pathname.split('/').filter(Boolean);
    if (paths.length > 0) {
      return [`/${paths[0]}`];
    }
    return [];
  }, [location.pathname]);

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={siderCollapsed}
      onCollapse={onCollapse}
      className={styles.sidebar}
      width={260}
      collapsedWidth={80}
    >
      <div className={styles.logo} onClick={() => navigate(ROUTES.DASHBOARD)}>
        <span className={styles.logoBolt} style={boltStyle}>
          <BoltIcon />
        </span>
        <span className={styles.logoText} style={textStyle}>
          {LOGO_TEXT}
        </span>
      </div>

      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        defaultOpenKeys={openKeys}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          background: 'transparent',
          borderRight: 'none',
        }}
        inlineCollapsed={siderCollapsed}
      />
    </Sider>
  );
};

export default Sidebar;